import { useEffect, useRef, useState, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiGet, apiSend } from '@/lib/api';
import {
  FLIGHT_TYPES,
  isExcludedAffiliation,
  affiliationLabel,
  FlightTypeCode,
  SoulAffiliationCode,
} from '@/lib/flightTypes';
import { DEFAULT_OPERATOR } from '../../../api/src/constants';
import { flightTimeProblem } from '@/lib/flightPatch';
import { distanceLabel, placeLabel, type RouteFacts } from '@/lib/summary';
import { PageHeader, Card, Field, ErrorNote, inputClass } from '@/components/ui';
import { ManifestRowFields, ExcludedChip, type ManifestEntry } from '@/components/ManifestRow';

interface Pilot {
  id: string;
  name: string;
  email?: string | null;
  initials?: string | null;
  affiliation: SoulAffiliationCode;
  is_owner: boolean;
}
interface Saved { id: string; full_name: string; initials: string | null; relationship: string | null }

function nowLocal() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}
function isoToLocal(iso: string) {
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}
function initialsFrom(name: string) {
  return name.split(/\s+/).map((s) => s.replace(/[^A-Za-z]/g, '')[0]).filter(Boolean).join('').toUpperCase();
}

/** The manifest row a pilot on file would occupy as the person flying this leg. */
function pilotEntry(pilot: Pilot, category: FlightTypeCode): ManifestEntry {
  return {
    initials: pilot.initials || initialsFrom(pilot.name),
    full_name: pilot.name,
    relationship: '',
    category,
    role_aboard: 'PIC',
    affiliation: pilot.affiliation,
  };
}

export default function NewFlight() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = (location.state as { prefill?: Record<string, unknown> } | null)?.prefill;
  const detectionId = (location.state as { detectionId?: string } | null)?.detectionId;
  const [pilots, setPilots] = useState<Pilot[]>([]);
  const [roster, setRoster] = useState<Saved[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // FlightAware's measured airborne time, where a detection exists. Offered as the
  // prefill and nothing more: flight time is what the owner types, and a zero
  // (AeroAPI caught only the taxi) is no figure at all.
  const airborne = Number(prefill?.airborne_hours) > 0 ? Number(prefill?.airborne_hours) : null;
  const flightawareId = (prefill?.flightaware_id as string | undefined) || undefined;

  const [form, setForm] = useState({
    date: prefill?.date ? isoToLocal(prefill.date as string) : nowLocal(),
    departure_airport: (prefill?.departure_airport as string) || '',
    arrival_airport: (prefill?.arrival_airport as string) || '',
    flight_hours: airborne != null ? String(airborne) : '',
    flight_type: 'B' as FlightTypeCode, operator: DEFAULT_OPERATOR, notes: '',
  });
  const [route, setRoute] = useState<RouteFacts | null>(null);

  // The manifest IS the record of who was aboard — the counts BA Law wants are
  // derived from it by the API, not typed here. The owner pilot is seeded onto
  // it because he is usually flying, and removable because he is not always.
  const [manifest, setManifest] = useState<ManifestEntry[]>([]);
  const [flownBy, setFlownBy] = useState<string>('');
  const [addingPilot, setAddingPilot] = useState(false);
  const [newPilot, setNewPilot] = useState({ name: '', initials: '', affiliation: 'hired_pilot' as SoulAffiliationCode });

  // Where the leg goes and how far, looked up as the codes are typed.
  useEffect(() => {
    const ok = (c: string) => /^[A-Z0-9]{3,4}$/.test(c);
    if (!ok(form.departure_airport) || !ok(form.arrival_airport)) {
      setRoute(null);
      return;
    }
    const q = `from=${encodeURIComponent(form.departure_airport)}&to=${encodeURIComponent(form.arrival_airport)}`;
    let live = true;
    apiGet<RouteFacts>(`/api/airports/route?${q}`).then((r) => live && setRoute(r)).catch(() => live && setRoute(null));
    return () => { live = false; };
  }, [form.departure_airport, form.arrival_airport]);

  useEffect(() => {
    apiGet<{ pilots: Pilot[] }>('/api/pilots')
      .then((d) => {
        const list = d.pilots || [];
        setPilots(list);
        // Default to the owner BY IDENTITY. Defaulting to the first row looks
        // right with one pilot on file and silently becomes whoever sorts first
        // with two — a defect that would mislabel a flight quietly.
        const owner = list.find((p) => p.is_owner);
        if (owner) {
          setFlownBy(owner.id);
          setManifest((m) => (m.length === 0 ? [pilotEntry(owner, 'B')] : m));
        }
      })
      .catch((e) => setError(e.message));
    apiGet<{ roster: Saved[] }>('/api/roster').then((d) => setRoster(d.roster || [])).catch(() => {});
  }, []);

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  // A seat's purpose is usually the leg's, so changing the flight type carries
  // the rows that still agreed with it — and leaves alone any seat deliberately
  // filed under something else.
  const previousType = useRef(form.flight_type);
  useEffect(() => {
    const was = previousType.current;
    previousType.current = form.flight_type;
    if (was === form.flight_type) return;
    setManifest((rows) => rows.map((r) => (r.category === was ? { ...r, category: form.flight_type } : r)));
  }, [form.flight_type]);

  /**
   * Changing who flew it rewrites the PIC row rather than storing a second claim
   * beside it. Exactly one soul holds PIC — the API and the database both say so.
   */
  function changeFlownBy(pilotId: string) {
    setFlownBy(pilotId);
    setManifest((rows) => {
      const rest = rows.filter((r) => r.role_aboard !== 'PIC');
      const pilot = pilots.find((p) => p.id === pilotId);
      return pilot ? [pilotEntry(pilot, form.flight_type), ...rest] : rest;
    });
  }

  async function addPilot() {
    if (!newPilot.name.trim()) return;
    try {
      const created = await apiSend<Pilot>('/api/pilots', 'POST', {
        name: newPilot.name.trim(),
        initials: newPilot.initials.trim() || undefined,
        affiliation: newPilot.affiliation,
      });
      setPilots((p) => [...p, created]);
      setNewPilot({ name: '', initials: '', affiliation: 'hired_pilot' });
      setAddingPilot(false);
      changeFlownBy(created.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add that pilot');
    }
  }

  const addBlank = () =>
    setManifest((m) => [
      ...m,
      { initials: '', full_name: '', relationship: '', category: form.flight_type, role_aboard: 'passenger', affiliation: 'guest' },
    ]);

  function addFromRoster(s: Saved) {
    if (manifest.some((r) => r.full_name === s.full_name)) return;
    setManifest((m) => [
      ...m,
      {
        initials: s.initials || '',
        full_name: s.full_name,
        relationship: s.relationship || '',
        category: form.flight_type,
        role_aboard: 'passenger',
        affiliation: 'guest',
      },
    ]);
  }

  const updateRow = <K extends keyof ManifestEntry>(i: number, k: K, v: ManifestEntry[K]) =>
    setManifest((m) => m.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const removeRow = (i: number) => setManifest((m) => m.filter((_, idx) => idx !== i));

  // Column F on BA Law's sheet is a locked =SUM(G:M); this is that same sum,
  // previewed. The API derives it again from the rows it actually stores, so the
  // saved record cannot disagree with what was shown here.
  const counted = manifest.filter((r) => !isExcludedAffiliation(r.affiliation));
  const excluded = manifest.filter((r) => isExcludedAffiliation(r.affiliation));
  const notesMentionExcluded = excluded.every(
    (r) => r.full_name && form.notes.toLowerCase().includes(r.full_name.toLowerCase().split(' ')[0])
  );

  function notesSuggestion() {
    const who = excluded
      .map((r) => `${r.full_name || r.initials} (${affiliationLabel(r.affiliation).toLowerCase()}, not in headcount)`)
      .join('; ');
    return form.notes ? `${form.notes.trimEnd()} ${who}.` : `${who}.`;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    const okCode = (c: string) => c.length >= 3 && c.length <= 4;
    if (!okCode(form.departure_airport) || !okCode(form.arrival_airport)) return setError('Departure and arrival must be 3–4 char airport codes (e.g. KDPA).');
    const timeProblem = flightTimeProblem(form.flight_hours);
    if (timeProblem) return setError(timeProblem);
    const hours = Number(form.flight_hours);
    const named = manifest.filter((r) => r.initials || r.full_name);
    if (named.length === 0) return setError('Record who was aboard — the aeroplane does not fly itself.');
    const pics = named.filter((r) => r.role_aboard === 'PIC').length;
    if (pics !== 1) {
      return setError(
        pics === 0
          ? 'Somebody had to be pilot in command. Give one soul that role.'
          : 'Only one soul can be pilot in command on a leg.'
      );
    }

    // Flight and manifest go in one request: a flight without a PIC is not a
    // legal record, and the API writes both inside one transaction so a half-
    // saved leg cannot exist.
    const payload = {
      date: new Date(form.date).toISOString(),
      departure_airport: form.departure_airport.toUpperCase(),
      arrival_airport: form.arrival_airport.toUpperCase(),
      flight_hours: hours, flight_type: form.flight_type,
      // Links the leg to the detection it came from, so the edit page offers the
      // same measured figure later.
      flightaware_id: flightawareId,
      operator: form.operator, // '' is sent as-is: cleared means none, absent means the API default
      notes: form.notes || undefined,
      souls: named.map((row) => ({
        initials: row.initials || initialsFrom(row.full_name),
        full_name: row.full_name || row.initials,
        relationship: row.relationship || undefined,
        category: row.category,
        role_aboard: row.role_aboard,
        affiliation: row.affiliation,
        pilot_id: pilots.find((p) => p.name === row.full_name)?.id,
      })),
    };

    setBusy(true);
    try {
      const flight = await apiSend<{ id: string }>('/api/flights', 'POST', payload);
      // If this came from a FlightAware detection, mark it logged so it won't resurface.
      if (detectionId) {
        await apiSend(`/api/flightaware/detected/${detectionId}/logged`, 'POST').catch(() => {});
      }

      // A leg flown jointly with the co-owner is this one row, at the whole leg's
      // flight time — there is no share to split off any more.
      navigate(`/flights/${flight.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save flight');
      setBusy(false);
    }
  }

  const availableRoster = roster.filter((s) => !manifest.some((r) => r.full_name === s.full_name));

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6 lg:p-8">
      <PageHeader title="Log a Flight" subtitle="Record a flight in N313G — and everyone who was aboard it." />
      <form onSubmit={onSubmit} className="space-y-6">
        {error && <ErrorNote message={error} />}

        <Card className="space-y-5 p-5 sm:p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Date & time"><input type="datetime-local" required value={form.date} onChange={(e) => set('date', e.target.value)} className={inputClass} /></Field>
            <Field label="Flown by" hint="Goes on the manifest as PIC. Not you? Pick who flew it, or add them.">
              <select value={flownBy} onChange={(e) => changeFlownBy(e.target.value)} className={inputClass}>
                <option value="" className="bg-night-800">— nobody on file —</option>
                {pilots.map((p) => <option key={p.id} value={p.id} className="bg-night-800">{p.name}</option>)}
              </select>
            </Field>
            <Field label="Departure" hint="ICAO code (e.g. KDPA)"><input required maxLength={4} value={form.departure_airport} onChange={(e) => set('departure_airport', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} className={`${inputClass} ident`} placeholder="KDPA" /></Field>
            <Field label="Arrival" hint="ICAO code (e.g. KTVC)"><input required maxLength={4} value={form.arrival_airport} onChange={(e) => set('arrival_airport', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} className={`${inputClass} ident`} placeholder="KTVC" /></Field>
            <div className="sm:col-span-2">
              {route && (
                <p className="text-sm text-slate-400">
                  <span className="text-slate-500">Destination </span>
                  <span className="text-slate-200">{placeLabel(route.destination) ?? `${form.arrival_airport} — not in the airport table`}</span>
                  <span className="text-slate-500"> · </span>
                  <span className="tabular-nums text-slate-200">{distanceLabel(route)}</span>
                </p>
              )}
            </div>
            <div>
              <Field label="Flight time (hrs)" hint="Required — the time you flew, as you would log it">
                <input required type="number" step="0.01" min="0.01" max="24" inputMode="decimal" value={form.flight_hours} onChange={(e) => set('flight_hours', e.target.value)} className={inputClass} placeholder="1.5" />
              </Field>
              {airborne != null && (
                <p className="mt-1.5 text-xs text-slate-500">
                  Filled in from FlightAware's measured airborne time,{' '}
                  <span className="tabular-nums text-slate-400">{airborne.toFixed(2)} h</span> (wheels-up → wheels-down).
                  Change it to what you flew.
                  {form.flight_hours !== String(airborne) && (
                    <>
                      {' '}
                      <button type="button" onClick={() => set('flight_hours', String(airborne))} className="text-avio underline underline-offset-2 hover:text-avio/80">use it again</button>
                    </>
                  )}
                </p>
              )}
            </div>
            <Field label="Flight type">
              <select value={form.flight_type} onChange={(e) => set('flight_type', e.target.value)} className={inputClass}>
                {FLIGHT_TYPES.map((t) => <option key={t.code} value={t.code} className="bg-night-800">{t.code} · {t.label}</option>)}
              </select>
            </Field>
          </div>

          {!addingPilot ? (
            <button type="button" onClick={() => setAddingPilot(true)} className="btn-ghost text-xs">
              + Add a pilot (co-owner, instructor, hired ferry pilot)
            </button>
          ) : (
            <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
              <div className="grid gap-2 sm:grid-cols-[2fr_1fr_2fr_auto]">
                <input placeholder="Full name" aria-label="Pilot name" value={newPilot.name} onChange={(e) => setNewPilot((s) => ({ ...s, name: e.target.value }))} className={inputClass} />
                <input placeholder="Initials" aria-label="Pilot initials" value={newPilot.initials} onChange={(e) => setNewPilot((s) => ({ ...s, initials: e.target.value.toUpperCase() }))} className={inputClass} />
                <select aria-label="What they are to the operation" value={newPilot.affiliation} onChange={(e) => setNewPilot((s) => ({ ...s, affiliation: e.target.value as SoulAffiliationCode }))} className={inputClass}>
                  <option value="co_owner" className="bg-night-800">Co-owner — counts</option>
                  <option value="hired_pilot" className="bg-night-800">Hired professional pilot — not counted</option>
                  <option value="hired_instructor" className="bg-night-800">Instructor (professional) — not counted</option>
                </select>
                <button type="button" onClick={addPilot} className="btn-ghost text-sm">Add</button>
              </div>
              <p className="mt-2 text-xs text-slate-500">No email needed — a ferry pilot is a name, some initials, and what they are to the operation.</p>
            </div>
          )}

          <Field label="Operator" hint="Optional — e.g. the business the flight was for"><input value={form.operator} onChange={(e) => set('operator', e.target.value)} className={inputClass} /></Field>
          <Field label="Notes" hint="Business purpose, remarks — important for tax substantiation"><textarea rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} className={inputClass} placeholder="Customer site visit…" /></Field>

        </Card>

        {/* Souls on board */}
        <Card className="space-y-4 p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">Souls on board</h2>
              <p className="text-xs text-slate-500">
                Everyone physically aboard, the pilot included. BA Law's headcount is derived from this —
                instructors, hired pilots and lap children stay on the record but out of the count.
              </p>
            </div>
            <button type="button" onClick={addBlank} className="btn-ghost text-sm">+ Add</button>
          </div>

          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-sm">
            <span className="text-slate-400">Headcount for BA Law:</span>
            <span className={`tabular-nums font-semibold ${counted.length === 0 ? 'text-red-300' : 'text-white'}`}>
              {counted.length}
            </span>
            {excluded.length > 0 && (
              <span className="text-xs text-slate-500">({excluded.length} aboard but not counted)</span>
            )}
            {counted.length === 0 && manifest.length > 0 && (
              <span className="text-xs text-red-300">
                — everyone aboard is excluded. BA Law's sheet will not accept a zero; their answer is pending.
              </span>
            )}
          </div>

          {excluded.length > 0 && !notesMentionExcluded && (
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/[0.06] p-3 text-xs text-amber-200">
              BA Law asks that instructors and professional pilots aboard be named in the Notes for the leg.{' '}
              <button type="button" onClick={() => set('notes', notesSuggestion())} className="underline underline-offset-2">
                add them to Notes
              </button>
            </div>
          )}

          {/* Roster quick-pick */}
          {availableRoster.length > 0 && (
            <div>
              <p className="mb-1.5 text-xs text-slate-500">Add from roster:</p>
              <div className="flex flex-wrap gap-2">
                {availableRoster.map((s) => (
                  <button key={s.id} type="button" onClick={() => addFromRoster(s)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-xs text-slate-300 ring-1 ring-white/10 transition hover:bg-avio/15 hover:text-white">
                    <span className="grid h-4 w-4 place-items-center rounded-full bg-avio/20 text-[9px] font-semibold text-avio">{s.initials || s.full_name[0]}</span>
                    {s.full_name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {manifest.length === 0 ? (
            <p className="text-sm text-red-300">Nobody on the manifest — record who was aboard.</p>
          ) : (
            <div className="space-y-3">
              {manifest.map((row, i) => (
                <div key={i} className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-xs text-slate-500">{row.full_name || 'New soul'}</span>
                    <ExcludedChip affiliation={row.affiliation} />
                  </div>
                  <ManifestRowFields
                    row={row}
                    onChange={(k, v) => updateRow(i, k, v)}
                    onRemove={() => removeRow(i)}
                  />
                </div>
              ))}
            </div>
          )}
        </Card>

        <div className="flex gap-3">
          <button type="submit" disabled={busy} className="btn-primary">{busy ? 'Saving…' : 'Save flight'}</button>
          <button type="button" onClick={() => navigate('/flights')} className="btn-ghost">Cancel</button>
        </div>
      </form>
    </div>
  );
}
